import 'react-quill-new/dist/quill.snow.css';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';
import CSSModule from 'react-css-modules';
import { injectIntl } from 'react-intl';
import ReactQuill from 'react-quill-new';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { AppWrapper, Block, Feedback, Spinner } from 'components';
import Breadcrumbs from 'components/BreadCrumbs/BreadCrumbs';
import AuthProtected from 'containers/AuthProtected/AuthProtected';
import { actions as blogActions } from 'coupon-common/src/modules/admin/blogManager';
import { actions as locationActions } from 'coupon-common/src/modules/locations';

import styles from './BlogManager.module.scss';
import messages from './messages';

const STATUS_DRAFT = 'draft';
const STATUS_PUBLISHED = 'published';

export const convertYoutubeLinksToIframes = html => {
  return html.replace(
    /<a[^>]*href="(https:\/\/www\.youtube\.com\/embed\/[^"]+)"[^>]*>.*?<\/a>/gi,
    (_, url) =>
      `<iframe
        class="ql-video"
        frameborder="0"
        allowfullscreen="true"
        src="${url}">
      </iframe>`,
  );
};

const QUILL_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'blockquote',
  'code-block',
  'link',
  'image',
  'video',
];

const getBlogIdFromQuery = search => {
  const params = new URLSearchParams(search);
  return params.get('id');
};

const getInitialForm = () => ({
  title: '',
  author: '',
  location_city: '',
  location_business: '',
  content_pillar: 'b2c',
  hero_image: '',
  reading_time: 0,
  publish_datetime: '',
  body: '',
  status: STATUS_DRAFT,
  views: 0,
  shares: 0,
  tags: '',
  links: '',
});

const BlogManager = ({
  intl,
  dispatch,
  blogs,
  blogDetail,
  saveState,
  imageUploadState,
  authors,
  businesses,
  locations,
}) => {
  const quillRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const blogId = getBlogIdFromQuery(location.search);
  const isEditing = !!blogId;
  const getArrayData = data => {
    if (Array.isArray(data)) {
      return data;
    }

    if (data && Array.isArray(data.results)) {
      return data.results;
    }

    return [];
  };

  const blogsList = getArrayData(blogs && blogs.data);
  const authorsList = getArrayData(authors && authors.data);
  const businessesList = getArrayData(businesses && businesses.data);

  const getLocationList = key => {
    const result =
      locations &&
      locations.result &&
      locations.result[key] &&
      locations.result[key].results
        ? locations.result[key].results
        : [];
  
    const entities =
      locations &&
      locations.entities &&
      locations.entities[key]
        ? locations.entities[key]
        : {};
  
    return result
      .map(id => entities[id])
      .filter(Boolean);
  };
  const provincesList = getLocationList('provinces');
  const citiesList = getLocationList('cities');

  const [form, setForm] = useState(getInitialForm());
  const [locationType, setLocationType] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [imageMode, setImageMode] = useState('url');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);
  useEffect(() => {
    // Reset image selection when switching blogs
  setImageMode('url');
  setSelectedImage(null);
  setImagePreview('');
    dispatch(blogActions.getBlogs());
    dispatch(blogActions.getBlogAuthors());
    dispatch(blogActions.getBlogBusinesses());
  
    dispatch(locationActions.getProvinces());
    dispatch(locationActions.getCities());
  
    if (blogId) {
      dispatch(blogActions.getBlog(blogId));
    } else {
      dispatch(blogActions.resetBlogDetail());
      setForm(getInitialForm());
      setLocationType('');
      setSelectedProvince('');
    }
  }, [dispatch, blogId]);

  useEffect(() => {
    if (blogDetail && blogDetail.data && isEditing) {
      const blog = blogDetail.data;
  
      setForm({
        ...getInitialForm(),
        ...blog,
        author: blog.author || '',
        location_city: blog.location_city || '',
        location_business: blog.location_business || '',
        body: convertYoutubeLinksToIframes(blog.body),
        publish_datetime: blog.publish_datetime
          ? blog.publish_datetime.slice(0, 16)
          : '',
      });
  
      if (blog.location_business) {
        setLocationType('business');
      } else if (blog.location_city) {
        setLocationType('city');
      } else {
        setLocationType('');
      }
    }
  }, [blogDetail && blogDetail.data, isEditing]);

  useEffect(() => {
    if (
      !isEditing ||
      !form.location_city ||
      !citiesList.length ||
      selectedProvince
    ) {
      return;
    }
  
    const selectedCity = citiesList.find(
      city =>
        Number(city.id || city.pk) ===
        Number(form.location_city),
    );
  
    if (
      selectedCity &&
      selectedCity.province &&
      selectedCity.province.name
    ) {
      setSelectedProvince(selectedCity.province.name);
  
      dispatch(
        locationActions.getCities(
          selectedCity.province.name,
        ),
      );
    }
  }, [
    citiesList,
    dispatch,
    form.location_city,
    isEditing,
    selectedProvince,
  ]);

  const handleImageModeChange = event => {
    const mode = event.target.value;
    setImageMode(mode);
    setSelectedImage(null);
    setImagePreview('');
  };
  const handleImageSelect = event => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      event.target.value = '';
      return;
    }
    const MAX_SIZE = 2 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      alert('Please select an image smaller than 2 MB.');
      event.target.value = '';
      return;
    }
  
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleChange = event => {
    const { name, value } = event.target;
  
    const numericFields = [
      'reading_time',
      'views',
      'shares',
      'author',
      'location_city',
      'location_business',
    ];
  
    setForm(prevForm => ({
      ...prevForm,
      [name]:
        numericFields.includes(name) && value !== ''
          ? Number(value)
          : value,
    }));
  };

  const handleProvinceChange = event => {
    const provinceName = event.target.value;
  
    setSelectedProvince(provinceName);
  
    setForm(prevForm => ({
      ...prevForm,
      location_city: '',
    }));
  
    if (provinceName) {
      dispatch(locationActions.getCities(provinceName));
    }
  };

  const handleLocationTypeChange = event => {
    const { value } = event.target;
  
    setLocationType(value);
    setSelectedProvince('');
  
    setForm(prevForm => ({
      ...prevForm,
      location_city: '',
      location_business: '',
    }));
  
    if (value === 'city') {
      dispatch(locationActions.getProvinces());
    }
  };

  const handleBodyChange = (value) => {


    setForm(prevForm => ({
      ...prevForm,
      body: value,
    }));
  };

  const buildPayload = status => ({
    ...form,
    status,
  
    author: form.author
      ? Number(form.author)
      : null,
  
    location_city:
      locationType === 'city' && form.location_city
        ? Number(form.location_city)
        : null,
  
    location_business:
      locationType === 'business' && form.location_business
        ? Number(form.location_business)
        : null,
  
    content_pillar: form.content_pillar || 'b2c',
  
    publish_datetime: form.publish_datetime || null,
  });

  const handleSaveDraft = () => {
    const payload = buildPayload(STATUS_DRAFT);
    if (imageMode === 'upload' && selectedImage) {
      payload.hero_image = isEditing ? form.hero_image : '';
    }
    const image = imageMode === 'upload' ? selectedImage : null;
    if (isEditing) {
      dispatch(blogActions.updateBlog(blogId, payload, image));
    } else {
      dispatch(blogActions.createBlog(payload, image));
    }
  };

  const handlePublish = () => {
    const payload = buildPayload(STATUS_PUBLISHED);
    if (imageMode === 'upload' && selectedImage) {
      payload.hero_image = isEditing ? form.hero_image : '';
    }
    const image = imageMode === 'upload' ? selectedImage : null;
    if (isEditing) {
      dispatch(blogActions.updateBlog(blogId, payload, image));
    } else {
      dispatch(blogActions.createBlog(payload, image));
    }
  };

  const handleEditExisting = id => {
    navigate(`/admin/blogs?id=${id}`);
  };

  const handleNewBlog = () => {
    // Reset all blog form fields
    setForm(getInitialForm());
  
    // Reset location fields
    setLocationType('');
    setSelectedProvince('');
  
    // Reset image upload fields
    setImageMode('url');
    setSelectedImage(null);
    setImagePreview('');
  
    // Clear previous blog details
    dispatch(blogActions.resetBlogDetail());
  
    // Navigate to the new blog form
    navigate('/admin/blogs');
  };

  const imageHandler = useCallback(function () {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');

    input.click();

    input.onchange = () => {
      const file = input.files?.[0];

      if (!file) return;

      const MAX_SIZE = 2 * 1024 * 1024; // 2 MB

      if (file.size > MAX_SIZE) {
        alert('Please select an image smaller than 2 MB.');
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);

        quill.insertEmbed(range.index, 'image', reader.result);
        quill.setSelection(range.index + 1);
      };

      reader.readAsDataURL(file);
    };
  }, []);


  const QUILL_MODULES = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: {
        image: imageHandler,
      },
    },
  }), []);

  return (
    <AppWrapper
      showHeader
      showSideNav
      showHeadingRow
      headingText={intl.formatMessage(messages.pageHeadingText)}
    >
      <AuthProtected />

      <div className="col-md-12">
        <Breadcrumbs locationArray={['Admin', 'Blog Manager']} />

        <div styleName="page-grid">
          <Block>
            <div styleName="form-header">
              <div>
                <h4>{isEditing ? 'Edit Blog' : 'Create Blog'}</h4>
                <p>Create, save drafts, and publish blog posts.</p>
              </div>

              {isEditing && (
                <button type="button" styleName="secondary-button" onClick={handleNewBlog}>
                  New Blog
                </button>
              )}
            </div>

            {blogDetail && blogDetail.fetching ? (
              <Spinner show />
            ) : (
              <div styleName="form">
                <div styleName="two-columns">
                  <label>
                    Title
                    <input name="title" value={form.title} onChange={handleChange} />
                  </label>

                  {/* <label>
                    Author
                    <input name="author" value={form.author} onChange={handleChange} />
                  </label> */}
                  <label>
                    Author Profile
                    <select name="author" value={form.author || ''} onChange={handleChange}>
                      <option value="">Select author profile</option>
                      {authorsList.map(author => (
                        <option key={author.id} value={author.id}>
                          {author.display_name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div styleName="two-columns">
                  {/* <label>
                    Author Link
                    <input name="author_link" value={form.author_link || ''} onChange={handleChange} />
                  </label> */}

                  <label>
                    Reading Time
                    <input type="number" min="0" name="reading_time" value={form.reading_time || 0} onChange={handleChange} />
                  </label>
                </div>

                <div styleName="two-columns">
  <label>
    Cover Image Source
    <select value={imageMode} onChange={handleImageModeChange}>
      <option value="url">Provide Image URL</option>
      <option value="upload">Upload Image</option>
    </select>
  </label>
</div>

{imageMode === 'url' ? (
  <div styleName="two-columns">
    <label>
      Hero Image URL
      <input
        name="hero_image"
        value={form.hero_image || ''}
        onChange={handleChange}
        placeholder="https://example.com/image.jpg"
      />
    </label>
  </div>
) : (
  <div styleName="two-columns">
    <label>
      Upload Cover Image
      <input
        type="file"
        accept="image/*"
        onChange={handleImageSelect}
      />
      <small>Choose an image smaller than 2 MB.</small>
    </label>
  </div>
)}
               {(imagePreview || form.hero_image) && (
  <div styleName="image-preview-row">
    <div styleName="image-preview-card">
      <span>Hero Image Preview</span>
      <img
        src={imagePreview || form.hero_image}
        alt="Hero preview"
      />
    </div>
  </div>
)}
                <div styleName="two-columns">
                    <label>
                      Publish Date + Time
                      <input
                        type="datetime-local"
                        name="publish_datetime"
                        value={form.publish_datetime || ''}
                        onChange={handleChange}
                      />
                    </label>

                    <label>
                      Status
                      <select
                        name="status"
                        value={form.status}
                        onChange={handleChange}
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </label>
                  </div>

                  <div styleName="two-columns">
                    <label>
                      Content Pillar
                      <select
                        name="content_pillar"
                        value={form.content_pillar || 'b2c'}
                        onChange={handleChange}
                      >
                        <option value="b2c">B2C - Customers</option>
                        <option value="b2b">B2B - Businesses</option>
                      </select>
                    </label>

                    <label>
                      Location Type
                      <select
                        value={locationType}
                        onChange={handleLocationTypeChange}
                      >
                        <option value="">No specific location</option>
                        <option value="city">City / Province</option>
                        <option value="business">Local Business</option>
                      </select>
                    </label>
                  </div>
                  {locationType === 'city' && (
  <div styleName="two-columns">
    <label>
      Province
      <select
        value={selectedProvince}
        onChange={handleProvinceChange}
      >
        <option value="">Select province</option>

        {provincesList.map(province => (
          <option
            key={province.id || province.pk}
            value={province.name}
          >
            {province.name}
          </option>
        ))}
      </select>
    </label>

    <label>
      City
      <select
        name="location_city"
        value={form.location_city || ''}
        onChange={handleChange}
        disabled={!selectedProvince}
      >
        <option value="">
          {selectedProvince
            ? 'Select city'
            : 'Select province first'}
        </option>

        {citiesList.map(city => (
          <option
            key={city.id || city.pk}
            value={city.id || city.pk}
          >
            {city.name}
          </option>
        ))}
      </select>
    </label>
  </div>
)}
{locationType === 'business' && (
  <label>
    Local Business
    <select
      name="location_business"
      value={form.location_business || ''}
      onChange={handleChange}
    >
      <option value="">Select local business</option>

      {businessesList.map(business => (
        <option
          key={business.id}
          value={business.id}
        >
          {business.name}
        </option>
      ))}
    </select>
  </label>
)}
                <label>
                  Tags
                  <input
                    name="tags"
                    value={form.tags || ''}
                    onChange={handleChange}
                    placeholder="Example: local deals, savings, Halifax"
                  />
                </label>

                <label>
                  Links
                  <input
                    name="links"
                    value={form.links || ''}
                    onChange={handleChange}
                    placeholder="Comma separated links"
                  />
                </label>

                <label>
                  Body
                  <div styleName="body-editor">
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={form.body}
                      onChange={handleBodyChange}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                    />
                  </div>
                </label>

                <div styleName="button-row">
                  <button type="button" styleName="secondary-button" onClick={handleSaveDraft}>
                    Save Draft
                  </button>
                  <button type="button" styleName="primary-button" onClick={handlePublish}>
                    Publish Blog
                  </button>
                </div>

                <Feedback
                  show={!!(saveState && saveState.error)}
                  invalid
                  content={saveState && saveState.error}
                />
                <Feedback
                  show={!!(imageUploadState && imageUploadState.error)}
                  invalid
                  content={imageUploadState && imageUploadState.error}
                />
              </div>
            )}
          </Block>

          <Block>
            <div styleName="list-header">
              <h4>Existing Blogs</h4>
              <p>Open a blog to edit by ID.</p>
            </div>

            {blogs && blogs.fetching ? (
              <Spinner show />
            ) : (
              <div styleName="blog-list">
                {blogsList.map(blog => (
                  <button
                    type="button"
                    key={blog.id}
                    styleName="blog-card"
                    onClick={() => handleEditExisting(blog.id)}
                  >
                    <div>
                      <h5>{blog.title}</h5>
                      <p>
                        {(blog.author_details && blog.author_details.display_name) || 'No author selected'}
                      </p>
                    </div>
                    <span styleName={blog.status === 'published' ? 'status-published' : 'status-draft'}>
                      {blog.status}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </Block>
        </div>
      </div>
    </AppWrapper>
  );
};

BlogManager.propTypes = {
  intl: PropTypes.object,
  dispatch: PropTypes.func,
  blogs: PropTypes.object,
  blogDetail: PropTypes.object,
  saveState: PropTypes.object,
  imageUploadState: PropTypes.object,
  authors: PropTypes.object,
  businesses: PropTypes.object,
  locations: PropTypes.object,
};

const mapStateToProps = state => ({
  blogs: state.admin.blogManager.list,
  blogDetail: state.admin.blogManager.detail,
  saveState: state.admin.blogManager.save,
  imageUploadState: state.admin.blogManager.imageUpload,
  authors: state.admin.blogManager.authors,
  businesses: state.admin.blogManager.businesses,
  locations: state.locations,
});

export default connect(mapStateToProps)(
  injectIntl(CSSModule(BlogManager, styles, { allowMultiple: true })),
);